import gql from 'graphql-tag';

/* ============================================
   PAYLINE EVENT NAMES & CONSTANTS
   ============================================ */

export const PAYLINE_EVENTS = {
  // Widget -> Parent postMessage types
  SUCCESS: 'PAYLINE_SUCCESS',
  ERROR: 'PAYLINE_ERROR',
  CANCEL: 'PAYLINE_CANCEL',
  // Hauteur réelle du document du widget, postée en continu par le `srcDoc` pour que
  // le parent dimensionne son iframe. Cf. `PaylineWidgetFrame`.
  RESIZE: 'PAYLINE_RESIZE',
};

export const PAYLINE_APP_EVENTS = {
  // App-level window events (mapped from postMessage types)
  SUCCESS: 'payline-payment-success',
  FAILURE: 'payline-payment-failure',
  CANCEL: 'payline-payment-cancel',
};

export const PAYLINE_FLOW_TYPES = {
  CHECKOUT: 'checkout',
  SAVE_CARD: 'save-card',
  IMPRINT_PAYMENT: 'imprint', // pay with saved card (no widget)
};

export const PAYLINE_WIDGET_URL =
  process.env.REACT_APP_PAYLINE_WIDGET_URL || 'https://payment.cdn.payline.com';

export const PAYLINE_API_BASE_URL = process.env.REACT_APP_PAYLINE_API_BASE_URL || 'https://payment.payline.com';

/*
 * Origines du widget d'homologation Monext.
 *
 * Elles ne servent qu'aux golfs que le serveur a déclarés hors production, mais
 * elles doivent figurer dans les origines acceptées en permanence : le contrôle
 * d'origine du `postMessage` s'installe avant que le token, donc l'environnement,
 * ne soit connu. Ce sont deux domaines de Monext, pas une ouverture générale.
 */
export const PAYLINE_HOMOLOGATION_WIDGET_URL = 'https://homologation-payment.cdn.payline.com';
export const PAYLINE_HOMOLOGATION_API_BASE_URL = 'https://homologation-payment.payline.com';

export const PAYLINE_EXPECTED_ORIGINS = [
  PAYLINE_WIDGET_URL,
  PAYLINE_API_BASE_URL,
  PAYLINE_HOMOLOGATION_WIDGET_URL,
  PAYLINE_HOMOLOGATION_API_BASE_URL,
];

/*
 * Origine du widget à charger réellement.
 *
 * Priorité : ce que l'appelant impose, puis ce que le serveur a renvoyé avec le
 * dernier token, puis la valeur du build. Le dernier terme est le comportement
 * historique et reste celui de toute la production.
 */
/*
 * `window` et non `globalThis` : l'ESLint de react-scripts ne connaît pas
 * `globalThis` dans ses globales, et CRA traite une erreur de lint comme un échec
 * de build. Aucune perte, ce service ne tourne que dans un navigateur, où
 * `globalThis === window` - c'est bien la même valeur que celle posée par
 * `primapp-common` (features/Payline/widgetEnvironment.js).
 */
export const resolveWidgetUrl = (override = null) =>
  override
  || (typeof window !== 'undefined' ? window.__primaPaylineWidgetUrl : null)
  || PAYLINE_WIDGET_URL;

/**
 * Origine acceptable pour un `postMessage` Payline.
 *
 * Cinq écrans installaient leur propre écouteur `message` SANS aucun contrôle
 * d'origine : n'importe quel document capable de poster un message à la fenêtre
 * déclenchait le traitement de succès avec un token arbitraire (audit du 28/07).
 * Ce helper leur donne la même vérification que `setupPaylineListeners`.
 *
 * `null` / `'null'` restent acceptés : l'iframe du widget est montée en `srcDoc`,
 * et une iframe srcDoc sans `allow-same-origin` poste depuis une origine opaque.
 * Les durcir ici casserait le paiement sur toute page qui retire ce flag.
 */
export const isExpectedPaylineOrigin = (origin) =>
  origin === window.location.origin
  || origin === null
  || origin === 'null'
  || PAYLINE_EXPECTED_ORIGINS.includes(origin);

/* ============================================
   GRAPHQL MUTATIONS & QUERIES
   ============================================ */

// `$action` est OBLIGATOIRE dans ce document, même s'il est optionnel côté schéma :
// sans lui le serveur retombe sur son défaut '101' (autorisation + validation), donc
// un DÉBIT RÉEL là où l'appelant croit poser une empreinte à 1 €. La variable
// manquait ici alors que les copies edg et hq du même fichier la déclarent
// (audit du 28/07). Ce service n'a aujourd'hui aucun appelant côté web - les écrans
// passent par primapp-common - mais toute reprise partirait avec le défaut piégé.
const PAYLINE_GET_TOKEN_MUTATION = gql`
  mutation PaylineGetToken(
    $amount: Int!
    $currency: String
    $reference: String!
    $returnUrl: String!
    $cancelUrl: String!
    $callbackUrl: String
    $customer: PaylineCustomer
    $walletId: String
    $action: String
  ) {
    paylineGetToken(
      amount: $amount
      currency: $currency
      reference: $reference
      returnUrl: $returnUrl
      cancelUrl: $cancelUrl
      callbackUrl: $callbackUrl
      customer: $customer
      walletId: $walletId
      action: $action
    ) {
      token
      expiresAt
      widgetUrl
    }
  }
`;

const PAYLINE_CHECK_PAYMENT_QUERY = gql`
  query PaylineCheckPayment($token: String!) {
    paylineCheckPayment(token: $token) {
      code
      status
      authorizationNumber
      transactionId
      maskedCardNumber
      cardBrand
      cardCountry
    }
  }
`;

const PAYLINE_CAPTURE_WITH_TOKEN_MUTATION = gql`
  mutation PaylineCaptureWithToken(
    $amount: Int!
    $currency: String
    $reference: String!
    $token: String!
    $uuid: String
    $customer: PaylineCustomer
  ) {
    paylineCaptureWithToken(
      amount: $amount
      currency: $currency
      reference: $reference
      token: $token
      uuid: $uuid
      customer: $customer
    ) {
      token
      resultCode
      transactionId
      authorizationNumber
      maskedCardNumber
      cardBrand
      expiryDate
      walletId
    }
  }
`;

const PAYLINE_CAPTURE_SUBSCRIBER_MUTATION = gql`
  mutation PaylineCaptureSubscriber(
    $amount: Int!
    $currency: String
    $reference: String!
    $subscriber: PaylineSubscriber!
  ) {
    paylineCaptureSubscriber(amount: $amount, currency: $currency, reference: $reference, subscriber: $subscriber) {
      resultCode
      transactionId
      authorizationNumber
      cardBrand
      maskedCardNumber
      needs3DS
      threeDSData {
        actionUrl
        actionMethod
        pareqFieldName
        pareqFieldValue
        mdFieldName
        mdFieldValue
        termUrl
        transientData
      }
    }
  }
`;

/* ============================================
   GRAPHQL API HELPERS
   ============================================ */

export const PaylineGraphQL = {
  /**
   * Request a Payline token for payment/card registration
   */
  async getToken(apolloClient, variables) {
    const { data } = await apolloClient.mutate({
      mutation: PAYLINE_GET_TOKEN_MUTATION,
      variables,
    });
    return data?.paylineGetToken || {};
  },

  /**
   * Verify/check a Payline payment result (after widget returns token)
   */
  async checkPayment(apolloClient, variables) {
    const { data } = await apolloClient.query({
      query: PAYLINE_CHECK_PAYMENT_QUERY,
      variables,
    });
    return data?.paylineCheckPayment || {};
  },

  /**
   * Capture with token (used for card registration / save card flow)
   */
  async captureWithToken(apolloClient, variables) {
    const { data } = await apolloClient.mutate({
      mutation: PAYLINE_CAPTURE_WITH_TOKEN_MUTATION,
      variables,
    });
    return data?.paylineCaptureWithToken || {};
  },

  /**
   * Capture with saved subscriber/token (pay with saved card)
   */
  async captureSubscriber(apolloClient, variables) {
    const { data } = await apolloClient.mutate({
      mutation: PAYLINE_CAPTURE_SUBSCRIBER_MUTATION,
      variables,
    });
    return data?.paylineCaptureSubscriber || {};
  },
};

/* ============================================
   PAYLINE WIDGET HTML GENERATOR
   ============================================ */

/**
 * Generate Payline widget iframe srcDoc HTML
 * @param {string} token - Payline token
 * @param {object} options - { flowType, amount, titleText, autosubmit, template }
 * @returns {string} HTML string for iframe srcDoc
 */
export function getPaylineHtml(token, options = {}) {
  const {
    flowType = PAYLINE_FLOW_TYPES.CHECKOUT,
    amount = null,
    titleText = 'Saisissez vos informations de carte',
    autosubmit = true,
    template = 'column',
    widgetUrl = null,
  } = options;

  // Origine du widget : celle imposée par l'appelant, sinon celle du dernier
  // token reçu, sinon celle du build. Cf. resolveWidgetUrl.
  const widgetBaseUrl = resolveWidgetUrl(widgetUrl);

  // Customize header based on flow type
  let headerBadge = '🔒 Paiement sécurisé Payline';
  let headerTitle = titleText;
  if (flowType === PAYLINE_FLOW_TYPES.SAVE_CARD) {
    headerBadge = '🔒 Enregistrement de carte Payline';
    headerTitle = 'Saisissez vos informations de carte';
  }

  const amountDisplay = amount ? `<div class="amount">Montant: €${(amount / 100).toFixed(2)}</div>` : '';

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no">
        <link rel="stylesheet" href="${widgetBaseUrl}/cdn/styles/widget-min.css">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: 0;
            padding: 0;
            background-color: transparent;
          }
          /*
           * Monext documente une largeur MINIMUM de 500 px pour #PaylineWidget
           * (max conseillé 900). On était à 480 - 2x20 de padding = 440 px, soit
           * 60 px sous le plancher : la page ACS de l'émetteur, calibrée pour 500,
           * débordait en largeur pendant le challenge 3DS.
           * Cf. docs/monext/05-widget-iframe.md §8.1.
           */
          .container {
            max-width: 620px;
            margin: 0 auto;
            padding: 20px;
            /*
             * Filet. La mise à l'échelle de la page ACS ci-dessous s'appuie sur un nom
             * de classe interne à Monext ; s'il change, elle cesse silencieusement de
             * s'appliquer. Ce débordement resterait alors enfermé dans le widget au
             * lieu de casser la mise en page de l'écran hôte.
             */
            overflow-x: auto;
          }
          /*
           * Sous 540 px, on rogne le padding plutôt que le widget : mieux vaut lui
           * laisser toute la largeur disponible. Monext bascule seul en colonne.
           */
          @media (max-width: 540px) {
            .container {
              padding: 8px;
            }
          }
          /*
           * Page ACS du challenge 3DS.
           *
           * Monext ne lui applique AUCUNE contrainte de largeur : dans widget-min.css,
           * la seule règle sur cette iframe est "overflow-y: auto". Elle s'affiche donc
           * à sa largeur naturelle et sort du conteneur dès que la place manque - ce qui
           * est structurel sur mobile, où la page ACS en veut environ 500 alors qu'un
           * téléphone en offre 375. Aucune largeur de conteneur ne peut régler ça.
           *
           * On la met donc à l'échelle plutôt que de la contraindre : contraindre la
           * ferait défiler à l'intérieur d'elle-même (domaine tiers, impossible à
           * reformater), la mettre à l'échelle garde tout visible. Facteur calculé à
           * l'exécution par fitAcsFrame().
           */
          .pl-redirectionEmbeddedIframe-view .pl-iframe-wrapper iframe {
            transform-origin: top left;
          }
          .header {
            text-align: center;
            padding: 20px;
            background: white;
            border-radius: 12px;
            margin-bottom: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .title {
            font-size: 20px;
            font-weight: bold;
            color: #0D212C;
            margin: 10px 0;
          }
          .secure-badge {
            font-size: 12px;
            color: #666;
            margin-bottom: 10px;
          }
          .amount {
            font-size: 18px;
            color: #0D7C2A;
            font-weight: 600;
            margin-top: 8px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="secure-badge">${headerBadge}</div>
            <div class="title">${headerTitle}</div>
            ${amountDisplay}
          </div>
          <div id="PaylineWidget" data-token="${token}" data-template="${template}" data-embeddedredirectionallowed="true" data-autosubmit="${autosubmit}" data-event-finalstatehasbeenreached="handleFinalState"></div>
        </div>
        <script>
          console.log('[Payline Widget] Page loaded, waiting for widget script...');
          
          function handleFinalState(event) {
            console.log('[Payline Widget] handleFinalState called:', event);
            const state = event && event.state ? event.state : '';
            console.log('[Payline Widget] State:', state);
            const typeMap = {
              'PAYMENT_SUCCESS': '${PAYLINE_EVENTS.SUCCESS}',
              'PAYMENT_FAILURE': '${PAYLINE_EVENTS.ERROR}',
              'PAYMENT_CANCELED': '${PAYLINE_EVENTS.CANCEL}'
            };
            const type = typeMap[state] || state;
            console.log('[Payline Widget] Posting message to parent:', { type, state });
            window.parent.postMessage(JSON.stringify({ type: type, data: { ...event, token: '${token}' } }), '*');
          }
          
          window.handleFinalState = handleFinalState;
          console.log('[Payline Widget] handleFinalState registered on window');

          /*
           * Hauteur réelle du contenu, postée au parent qui dimensionne son iframe.
           *
           * Sans cela l'iframe est figée (800 px sur 11 écrans) : trop haute pour le
           * formulaire carte, d'où un grand vide blanc, et trop basse pour la page ACS
           * du challenge 3DS, d'où un ascenseur imbriqué.
           *
           * Le seuil de 8 px sert d'hystérésis : un redimensionnement du parent change
           * la hauteur du contenu, qui reposterait, ce qui rebouclerait indéfiniment.
           */
          /*
           * Ajuste la page ACS du challenge 3DS à la largeur disponible.
           *
           * Ne fait rien tant que la place suffit : sur un écran large, aucune
           * transformation n'est appliquée.
           */
          var ACS_FRAME_SELECTOR = '.pl-redirectionEmbeddedIframe-view .pl-iframe-wrapper iframe';

          function fitAcsFrame() {
            var frame = document.querySelector(ACS_FRAME_SELECTOR);
            if (!frame || !frame.parentNode) return;

            var wrapper = frame.parentNode;

            /*
             * offsetWidth et offsetHeight IGNORENT les transformations CSS : on lit donc
             * toujours la taille voulue par Monext, jamais le résultat de notre propre
             * mise à l'échelle. Sans cette propriété, le facteur se réduirait à chaque
             * passe et l'ACS finirait par disparaître.
             */
            var naturalWidth = frame.offsetWidth;
            var naturalHeight = frame.offsetHeight;
            if (!naturalWidth || !naturalHeight) return;

            var available = wrapper.clientWidth || document.documentElement.clientWidth;
            var scale = available && available < naturalWidth ? available / naturalWidth : 1;

            if (scale >= 1) {
              frame.style.transform = '';
              wrapper.style.height = '';
              return;
            }

            frame.style.transform = 'scale(' + scale + ')';
            /*
             * Une transformation est purement visuelle : le flux garde la hauteur
             * d'origine. Sans cette correction, une bande vide resterait sous l'ACS.
             */
            wrapper.style.height = Math.ceil(naturalHeight * scale) + 'px';
          }

          var lastPostedHeight = 0;
          function postHeight() {
            var height = Math.ceil(document.documentElement.scrollHeight);
            if (!height || Math.abs(height - lastPostedHeight) < 8) return;
            lastPostedHeight = height;
            window.parent.postMessage(
              JSON.stringify({ type: '${PAYLINE_EVENTS.RESIZE}', data: { height: height } }),
              '*'
            );
          }

          // L'ajustement AVANT le report : il change la hauteur, et c'est la hauteur
          // corrigée qu'on veut envoyer au parent.
          function onLayoutChange() {
            fitAcsFrame();
            postHeight();
          }

          if (typeof ResizeObserver !== 'undefined') {
            new ResizeObserver(onLayoutChange).observe(document.documentElement);
          } else {
            // Repli pour les navigateurs sans ResizeObserver : le widget injecte son
            // contenu de façon asynchrone, un simple événement 'load' arriverait trop tôt.
            setInterval(onLayoutChange, 400);
          }
          window.addEventListener('load', onLayoutChange);
          // Rotation d'un téléphone, redimensionnement d'une fenêtre : la largeur
          // disponible change sans que le contenu bouge.
          window.addEventListener('resize', onLayoutChange);
        </script>
        <script src="${widgetBaseUrl}/cdn/scripts/widget-min.js" onload="console.log('[Payline Widget] Script loaded successfully')" onerror="console.error('[Payline Widget] Script failed to load')"></script>
      </body>
    </html>
  `;

  return html;
}

/* ============================================
   PAYLINE LISTENER SETUP & CLEANUP
   ============================================ */

/**
 * Setup unified Payline listeners
 * Binds to: window.postMessage, custom DOM events
 * Branches on flowType for success handling
 *
 * @param {string} flowType - 'checkout', 'save-card', or 'imprint'
 * @param {object} handlers - { onSuccess, onFailure, onCancel }
 * @returns {function} cleanup function to remove all listeners
 */
export function setupPaylineListeners(flowType, handlers = {}) {
  const { onSuccess, onFailure, onCancel } = handlers;
  const listeners = [];

  // Helper to bind and track listeners
  const bindListener = (target, eventName, handler) => {
    if (target === 'window') {
      window.addEventListener(eventName, handler);
      listeners.push(() => window.removeEventListener(eventName, handler));
    }
  };

  // Internal success handler (branches by flow type)
  const handleSuccess = (eventData) => {
    // Extract token from various payload formats
    const token = eventData?.token || eventData?.paylinetoken || null;

    // Branch on flow type for appropriate handling
    if (flowType === PAYLINE_FLOW_TYPES.SAVE_CARD && onSuccess) {
      // Save-card flow: let caller handle captureWithToken
      onSuccess(eventData, 'save-card');
    } else if (flowType === PAYLINE_FLOW_TYPES.CHECKOUT && onSuccess) {
      // Checkout flow: let caller handle checkPayment
      onSuccess(eventData, 'checkout');
    } else if (onSuccess) {
      // Generic fallback
      onSuccess(eventData, flowType);
    }
  };

  const handleFailure = (eventData) => {
    if (onFailure) onFailure(eventData);
  };

  const handleCancel = (eventData) => {
    if (onCancel) onCancel(eventData);
  };

  const handlePostMessage = (event) => {
    console.log('[Payline] postMessage received:', {
      origin: event.origin,
      data: event.data,
      source: event.source ? 'iframe' : 'window',
    });

    const isAllowedOrigin =
      event.origin === window.location.origin ||
      event.origin === null ||
      event.origin === 'null' ||
      PAYLINE_EXPECTED_ORIGINS.includes(event.origin);

    if (!isAllowedOrigin) {
      console.log('[Payline] Message rejected - origin:', event.origin);
      return;
    }

    try {
      const msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      console.log('[Payline] Parsed message:', msg);

      if (msg.type === PAYLINE_EVENTS.SUCCESS) {
        console.log('[Payline] SUCCESS event detected');
        handleSuccess(msg.data);
      } else if (msg.type === PAYLINE_EVENTS.ERROR) {
        console.log('[Payline] ERROR event detected');
        handleFailure(msg.data);
      } else if (msg.type === PAYLINE_EVENTS.CANCEL) {
        console.log('[Payline] CANCEL event detected');
        handleCancel(msg.data);
      } else if (msg.type === PAYLINE_EVENTS.RESIZE) {
        // Traité par `PaylineWidgetFrame`, pas par les écrans. Sorti explicitement
        // pour ne pas noyer la console sous des « Unknown message type ».
      } else {
        console.log('[Payline] Unknown message type:', msg.type);
      }
    } catch (err) {
      console.log('[Payline] Failed to parse message:', err);
    }
  };
  bindListener('window', 'message', handlePostMessage);

  const handleCustomSuccess = () => handleSuccess({});
  const handleCustomFailure = () => handleFailure({});
  const handleCustomCancel = () => handleCancel({});

  bindListener('window', PAYLINE_APP_EVENTS.SUCCESS, handleCustomSuccess);
  bindListener('window', PAYLINE_APP_EVENTS.FAILURE, handleCustomFailure);
  bindListener('window', PAYLINE_APP_EVENTS.CANCEL, handleCustomCancel);

  // Return cleanup function
  return () => {
    listeners.forEach((removeListener) => removeListener());
  };
}

/* ============================================
   EXPORTS
   ============================================ */

export default {
  PAYLINE_EVENTS,
  PAYLINE_APP_EVENTS,
  PAYLINE_FLOW_TYPES,
  PAYLINE_WIDGET_URL,
  PAYLINE_API_BASE_URL,
  GraphQL: PaylineGraphQL,
  getPaylineHtml,
  setupPaylineListeners,
};
